// Central sector registry — drives Home grid, sector landing pages and dynamic claim form steps.
export const SECTORS = [
    {
        slug: "pcp-motor-finance",
        name: "PCP & Motor Finance",
        short: "Mis-sold car finance",
        tagline: "FCA reviewing 1000s of mis-sold PCP & HP agreements — get your refund.",
        icon: "Car",
        hot: true,
        averagePayout: "£1,100 – £4,500",
        intro:
            "If you took out PCP, HP or another car finance agreement before 28 January 2021, you may be owed compensation. Hidden commission was widespread and the FCA has confirmed an industry-wide redress scheme is on the table.",
        bullets: [
            "Mis-sold PCP, HP, lease purchase or conditional sale agreements",
            "Discretionary commission arrangements (DCA) before Jan 2021",
            "Hidden or undisclosed broker commission of any kind",
            "Multiple agreements? Check each — they can all qualify",
        ],
        questions: [
            { key: "finance_type", label: "What type of finance did you take out?", type: "select", options: ["PCP", "HP / Conditional Sale", "Lease Purchase", "Personal Contract Hire", "Not sure"] },
            { key: "lender", label: "Which lender / dealer provided the finance?", type: "text", placeholder: "e.g. Black Horse, Santander Consumer, MotoNovo" },
            { key: "year_taken", label: "Approximate year you took out the agreement", type: "select", options: ["Before 2014", "2014 – 2017", "2018 – 2020", "2021", "After 2021"] },
            { key: "still_active", label: "Is the agreement still active?", type: "select", options: ["Yes, still paying", "Settled / paid off", "Voluntarily terminated", "Vehicle returned"] },
            { key: "approx_finance_amount", label: "Approximate amount financed (£)", type: "text", placeholder: "e.g. 14,500" },
        ],
    },
    {
        slug: "housing-disrepair",
        name: "Housing Disrepair (HDR)",
        short: "Damp, mould, leaks, broken heating",
        tagline: "If your landlord ignored repairs, you may be entitled to thousands in compensation.",
        icon: "Home",
        hot: true,
        averagePayout: "£1,500 – £12,000",
        intro:
            "Council, housing association and private tenants in England & Wales are protected under the Homes (Fitness for Human Habitation) Act. If your landlord hasn't fixed reported damp, mould, leaks, broken heating or structural damage, you can claim repairs and compensation.",
        bullets: [
            "Damp & black mould affecting walls, ceilings or belongings",
            "Persistent leaks, plumbing failures or rising damp",
            "Broken heating, hot water or electrical hazards",
            "Pest infestations, structural cracks, unsafe windows or doors",
        ],
        questions: [
            { key: "tenancy_type", label: "Who is your landlord?", type: "select", options: ["Local council", "Housing association", "Private landlord", "Not sure"] },
            { key: "years_at_property", label: "How long have you lived at the property?", type: "select", options: ["Less than 1 year", "1 – 3 years", "3 – 5 years", "5 – 10 years", "10+ years"] },
            { key: "issues", label: "What issues are you experiencing? (select all that apply)", type: "checkboxes", options: ["Damp & mould", "Leaks / plumbing", "Broken heating / hot water", "Electrical hazards", "Structural / cracks", "Pests / infestation", "Windows / doors", "Other"] },
            { key: "reported_when", label: "Have you reported it to your landlord?", type: "select", options: ["Yes, more than 6 months ago", "Yes, 1 – 6 months ago", "Yes, less than 1 month ago", "Not yet"] },
            { key: "health_impact", label: "Has the disrepair affected your health or possessions?", type: "select", options: ["Yes, health affected", "Yes, possessions damaged", "Both", "No"] },
        ],
    },
    {
        slug: "personal-injury",
        name: "Personal Injury",
        short: "Any injury that wasn't your fault",
        tagline: "If someone else's negligence caused your injury, you deserve compensation.",
        icon: "Activity",
        averagePayout: "£1,000 – £50,000+",
        intro:
            "Personal injury claims cover any accident that wasn't your fault — at work, on the road, in public, or as a result of someone else's negligence. We work with specialist solicitors on a strict No Win, No Fee basis.",
        bullets: [
            "100% No Win, No Fee — no risk to you",
            "Specialist injury solicitors regulated by the SRA",
            "Compensation for pain, lost earnings & rehabilitation",
            "Claims handled within the standard 3-year limit",
        ],
        questions: [
            { key: "accident_type", label: "What type of accident caused the injury?", type: "select", options: ["Road traffic", "At work", "In a public place", "Sport / leisure", "Assault / criminal injury", "Other"] },
            { key: "injury_date", label: "When did the accident happen?", type: "select", options: ["Last 3 months", "3 – 12 months ago", "1 – 2 years ago", "2 – 3 years ago", "Over 3 years ago"] },
            { key: "injury_severity", label: "How serious are your injuries?", type: "select", options: ["Minor — fully recovered", "Moderate — ongoing", "Serious — long-term effects", "Catastrophic / life-changing"] },
            { key: "medical_treatment", label: "Did you receive medical treatment?", type: "select", options: ["GP / A&E only", "Hospital admission", "Ongoing physio / specialist", "No treatment yet"] },
            { key: "time_off_work", label: "Have you taken time off work?", type: "select", options: ["No time off", "Up to 2 weeks", "2 weeks – 3 months", "More than 3 months"] },
        ],
    },
    {
        slug: "road-traffic-accident",
        name: "Road Traffic Accident",
        short: "Car, motorbike, cyclist or pedestrian",
        tagline: "Whether you were driving, riding, cycling or walking — claim what's owed.",
        icon: "Siren",
        averagePayout: "£1,500 – £25,000",
        intro:
            "Every year over 100,000 UK road users are injured in accidents that weren't their fault. Whether you were a driver, passenger, motorcyclist, cyclist or pedestrian, we can help you recover compensation for injuries, vehicle damage and lost income.",
        bullets: [
            "Whiplash, soft-tissue, fractures and serious injuries",
            "Vehicle damage, hire car costs and loss of use",
            "Lost earnings and rehabilitation costs",
            "Uninsured / hit-and-run claims via MIB",
        ],
        questions: [
            { key: "role", label: "You were the…", type: "select", options: ["Driver", "Passenger", "Motorcyclist", "Cyclist", "Pedestrian"] },
            { key: "accident_date", label: "When did the accident happen?", type: "select", options: ["Last month", "1 – 6 months ago", "6 – 12 months ago", "1 – 3 years ago", "Over 3 years"] },
            { key: "at_fault", label: "Were you at fault?", type: "select", options: ["No, the other driver was", "Partially", "Not sure", "Yes"] },
            { key: "police_reported", label: "Was the accident reported to police?", type: "select", options: ["Yes", "No", "Don't know"] },
            { key: "other_driver_insured", label: "Was the other driver insured?", type: "select", options: ["Yes", "No / uninsured", "Hit & run", "Not sure"] },
        ],
    },
    {
        slug: "medical-negligence",
        name: "Medical Negligence",
        short: "NHS, GP, dental or private care failures",
        tagline: "When medical care falls below an acceptable standard, you have the right to compensation.",
        icon: "Stethoscope",
        averagePayout: "£5,000 – £250,000+",
        intro:
            "Misdiagnosis, surgical errors, birth injuries, dental negligence — when healthcare professionals fail you or your family, we work with specialist clinical negligence solicitors to fight for full compensation and accountability.",
        bullets: [
            "Misdiagnosis or delayed diagnosis (incl. cancer)",
            "Surgical errors, never-events and post-op complications",
            "Birth injuries (mother or baby)",
            "Dental, cosmetic and private healthcare negligence",
        ],
        questions: [
            { key: "negligence_type", label: "What type of negligence are you claiming for?", type: "select", options: ["Misdiagnosis / delay", "Surgical error", "Birth injury", "Dental negligence", "Cosmetic procedure", "Medication error", "Other"] },
            { key: "provider", label: "Was the treatment NHS or private?", type: "select", options: ["NHS hospital", "NHS GP", "Private hospital / clinic", "Dental practice", "Not sure"] },
            { key: "incident_date", label: "When did the incident occur?", type: "select", options: ["Last 6 months", "6 – 18 months", "1 – 3 years", "Over 3 years"] },
            { key: "ongoing_impact", label: "Is there ongoing impact on your health?", type: "select", options: ["Yes, significant", "Yes, moderate", "Mild", "Recovered"] },
            { key: "complaint_made", label: "Have you raised a formal complaint with the provider?", type: "select", options: ["Yes", "No", "In progress"] },
        ],
    },
    {
        slug: "workplace-accident",
        name: "Workplace Accident",
        short: "Injured on the job — claim safely",
        tagline: "Your employer has a duty of care. If they breached it, you can claim without fear of dismissal.",
        icon: "Factory",
        averagePayout: "£2,500 – £45,000",
        intro:
            "All UK employers must carry Employer's Liability Insurance — designed precisely so you can claim compensation if injured at work. It is unlawful to dismiss or treat you unfairly for making a claim.",
        bullets: [
            "Slips, trips and falls in the workplace",
            "Lifting & manual handling injuries (back, shoulder)",
            "Faulty equipment, machinery or PPE",
            "Industrial disease (HAVS, asbestos, hearing loss)",
        ],
        questions: [
            { key: "industry", label: "What industry do you work in?", type: "select", options: ["Construction", "Manufacturing", "Warehouse / logistics", "Healthcare", "Hospitality", "Retail", "Office", "Other"] },
            { key: "accident_cause", label: "Main cause of the accident", type: "select", options: ["Slip / trip / fall", "Lifting / manual handling", "Faulty equipment", "No PPE provided", "Untrained on task", "Repetitive strain", "Other"] },
            { key: "reported", label: "Was the accident logged in the workplace accident book?", type: "select", options: ["Yes", "No", "Not sure"] },
            { key: "still_employed", label: "Are you still employed at the same company?", type: "select", options: ["Yes", "No, left", "On long-term sick", "Dismissed"] },
            { key: "time_off", label: "How much time have you taken off?", type: "select", options: ["None", "Up to 2 weeks", "2 weeks – 3 months", "More than 3 months"] },
        ],
    },
    {
        slug: "data-breach",
        name: "Data Breach & Privacy",
        short: "Your data leaked? Claim under GDPR",
        tagline: "Under UK GDPR you can claim compensation when an organisation loses, leaks or misuses your data.",
        icon: "ShieldAlert",
        averagePayout: "£750 – £8,000",
        intro:
            "If your personal data has been exposed in a cyber attack, lost in the post, sent to the wrong person, or accessed without authorisation, you have a right to claim — even without proving financial loss.",
        bullets: [
            "Cyber-attacks, ransomware and data leaks",
            "Misdirected emails, post or texts",
            "Unauthorised access by staff or third parties",
            "Failure to honour your GDPR rights",
        ],
        questions: [
            { key: "breach_type", label: "What kind of breach occurred?", type: "select", options: ["Cyber-attack / hack", "Misdirected email / letter", "Unauthorised staff access", "Lost / stolen device", "Other"] },
            { key: "organisation", label: "Which organisation was responsible?", type: "text", placeholder: "e.g. retailer name, NHS trust, council" },
            { key: "data_type", label: "What kind of data was exposed?", type: "select", options: ["Financial details", "Medical records", "Identity documents", "Contact details only", "Other"] },
            { key: "ico_reported", label: "Has the breach been reported to the ICO?", type: "select", options: ["Yes", "No", "Not sure"] },
            { key: "impact", label: "How has this affected you?", type: "select", options: ["Financial loss", "Identity theft / fraud", "Distress / anxiety", "Reputational damage", "None yet"] },
        ],
    },
    {
        slug: "flight-delay-holiday",
        name: "Flight Delay & Holiday Claims",
        short: "Delays, cancellations, holiday sickness",
        tagline: "Up to €600 per passenger for delays — plus full holiday sickness compensation.",
        icon: "Plane",
        averagePayout: "£250 – £4,000",
        intro:
            "Under UK261/EU261 you may be owed compensation for flights delayed by 3+ hours, cancelled flights, denied boarding and lost luggage. We also handle holiday sickness and package holiday claims.",
        bullets: [
            "Flight delays of 3+ hours within last 6 years",
            "Cancelled flights & denied boarding",
            "Holiday sickness at all-inclusive resorts",
            "Lost or damaged baggage",
        ],
        questions: [
            { key: "claim_type", label: "What are you claiming for?", type: "select", options: ["Flight delay 3+ hrs", "Cancelled flight", "Denied boarding", "Holiday sickness", "Lost luggage", "Other"] },
            { key: "airline", label: "Which airline / tour operator?", type: "text", placeholder: "e.g. TUI, easyJet, Jet2" },
            { key: "flight_date", label: "When did it happen?", type: "select", options: ["Last 6 months", "6 – 18 months", "1 – 3 years", "3 – 6 years", "Over 6 years"] },
            { key: "passengers", label: "How many passengers in your booking?", type: "select", options: ["1", "2", "3 – 4", "5+"] },
            { key: "departure", label: "Departure airport", type: "text", placeholder: "e.g. Manchester (MAN)" },
        ],
    },
    {
        slug: "slip-trip-public-liability",
        name: "Slip, Trip & Public Liability",
        short: "Accidents in public places",
        tagline: "Tripped on a broken pavement, supermarket floor or in a restaurant? You may have a claim.",
        icon: "Footprints",
        averagePayout: "£1,200 – £15,000",
        intro:
            "Councils, retailers and businesses owe you a duty of care under the Occupiers' Liability Act. If they failed to maintain safe premises and you were injured, you can claim compensation.",
        bullets: [
            "Tripping on broken / uneven pavements (council)",
            "Slips in supermarkets, restaurants, shops",
            "Falls in pubs, gyms, hotels and leisure venues",
            "Inadequate lighting, signage or maintenance",
        ],
        questions: [
            { key: "location_type", label: "Where did the accident happen?", type: "select", options: ["Pavement / road", "Supermarket / shop", "Restaurant / café", "Pub / nightclub", "Hotel / leisure", "Park / public space", "Other"] },
            { key: "cause", label: "What caused the accident?", type: "select", options: ["Wet / slippery floor", "Uneven surface / pothole", "Obstacles in walkway", "Poor lighting", "Defective equipment", "Other"] },
            { key: "reported", label: "Was the incident reported to staff / owner?", type: "select", options: ["Yes, with reference", "Yes, verbally only", "No"] },
            { key: "photos", label: "Did you take photos at the scene?", type: "select", options: ["Yes", "No"] },
            { key: "witnesses", label: "Are there any witnesses?", type: "select", options: ["Yes", "No", "Not sure"] },
        ],
    },
    {
        slug: "mis-sold-pensions",
        name: "Mis-Sold Pensions & Investments",
        short: "SIPP, FSAVC, final-salary transfers",
        tagline: "Mis-sold a pension transfer or unsuitable investment? Reclaim what's rightfully yours.",
        icon: "PiggyBank",
        averagePayout: "£10,000 – £150,000+",
        intro:
            "If you were advised to transfer a defined-benefit pension, invest in a SIPP with unregulated assets, or sold an unsuitable product, you may be entitled to substantial compensation via FOS or FSCS.",
        bullets: [
            "Defined-benefit / final-salary transfers",
            "SIPP investments in unregulated assets",
            "Mis-sold FSAVCs, AVCs or annuities",
            "Unsuitable investment advice from IFAs",
        ],
        questions: [
            { key: "product_type", label: "Type of product mis-sold", type: "select", options: ["DB / Final-salary transfer", "SIPP", "FSAVC / AVC", "Annuity", "Investment bond", "Other"] },
            { key: "advice_year", label: "Year you received the advice", type: "select", options: ["Before 2010", "2010 – 2015", "2015 – 2020", "After 2020"] },
            { key: "value_at_advice", label: "Approximate value at time of advice (£)", type: "select", options: ["Under £30k", "£30k – £100k", "£100k – £250k", "£250k+"] },
            { key: "adviser", label: "Adviser / firm name (if known)", type: "text", placeholder: "Optional — helps us check FCA register" },
            { key: "current_loss", label: "Have you suffered a loss?", type: "select", options: ["Yes, significant", "Yes, moderate", "Unsure", "Not yet"] },
        ],
    },
];

export const SECTOR_BY_SLUG = Object.fromEntries(SECTORS.map((s) => [s.slug, s]));
